import { useState } from "react";
import { Star, Send } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { useTheme } from "./ThemeInjector";
import { toast } from "sonner";

interface ReviewSectionProps {
  productId: number;
}

export function ReviewSection({ productId }: ReviewSectionProps) {
  const { siteConfig, reviews, addReview } = useAdminData();
  const { t } = useLanguage();
  const theme = useTheme();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");

  if (!siteConfig.features?.reviews) return null;

  const productReviews = reviews
    .filter((r) => r.product_id === productId && r.approved)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const avgRating =
    productReviews.length > 0
      ? productReviews.reduce((sum, r) => sum + r.rating, 0) / productReviews.length
      : 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const clampedRating = Math.max(1, Math.min(5, rating));
    addReview({
      id: `rev_${Date.now()}`,
      product_id: productId,
      name: name.slice(0, 80),
      rating: clampedRating,
      comment: comment.slice(0, 1000),
      approved: false,
      created_at: new Date().toISOString(),
    });
    toast.success(t("reviews.pending"));
    setName("");
    setRating(5);
    setComment("");
    setShowForm(false);
  };

  return (
    <section className="max-w-7xl mx-auto px-6 mt-24">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2
              className="tracking-[0.08em] mb-1"
              style={{ fontFamily: theme.fontHeading, fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 400, color: theme.fg }}
            >
              {t("reviews.title")}
            </h2>
            {productReviews.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={14}
                      className={star <= Math.round(avgRating) ? "" : ""}
                      style={{ color: star <= Math.round(avgRating) ? theme.accent : theme.border, fill: star <= Math.round(avgRating) ? theme.accent : "none" }}
                    />
                  ))}
                </div>
                <span
                  style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.muted }}
                >
                  ({productReviews.length})
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-5 py-2.5 border tracking-[0.15em] uppercase transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 400, borderColor: theme.primary, color: theme.primary, borderRadius: theme.btnRadius }}
          >
            {t("reviews.write")}
          </button>
        </div>

        {/* Write Review Form */}
        <AnimatePresence>
          {showForm && (
            <motion.form
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onSubmit={handleSubmit}
              className="overflow-hidden mb-10"
            >
              <div className="rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] space-y-5" style={{ backgroundColor: theme.card }}>
                {/* Rating stars */}
                <div>
                  <label
                    className="block tracking-[0.15em] uppercase mb-3"
                    style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.muted }}
                  >
                    Rating
                  </label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-0.5 focus:outline-none"
                        aria-label={`${star} star${star > 1 ? "s" : ""}`}
                      >
                        <Star
                          size={22}
                          className="transition-colors"
                          style={{ color: star <= (hoverRating || rating) ? theme.accent : theme.border, fill: star <= (hoverRating || rating) ? theme.accent : "none" }}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block tracking-[0.15em] uppercase mb-2"
                      style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.muted }}
                    >
                      {t("reviews.your_name")}
                    </label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={80}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors"
                      style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.bg, borderColor: theme.border, color: theme.fg }}
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block tracking-[0.15em] uppercase mb-2"
                    style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.muted }}
                  >
                    {t("reviews.your_review")}
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    maxLength={1000}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors resize-none"
                    style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.bg, borderColor: theme.border, color: theme.fg }}
                  />
                </div>

                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-3 tracking-[0.2em] uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{ fontFamily: theme.fontBody, fontSize: "11px", backgroundColor: theme.btnBg, color: theme.btnText, borderRadius: theme.btnRadius }}
                >
                  <Send size={14} />
                  {t("reviews.submit")}
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Reviews List */}
        {productReviews.length === 0 ? (
          <p
            className="text-center py-8"
            style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
          >
            {t("reviews.no_reviews")}
          </p>
        ) : (
          <div className="space-y-6">
            {productReviews.map((review) => (
              <div
                key={review.id}
                className="rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]"
                style={{ backgroundColor: theme.card }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p
                      className="mb-1"
                      style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 400, color: theme.fg }}
                    >
                      {review.name}
                    </p>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={12}
                          style={{ color: star <= review.rating ? theme.accent : theme.border, fill: star <= review.rating ? theme.accent : "none" }}
                        />
                      ))}
                    </div>
                  </div>
                  <span
                    style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 300, color: theme.border }}
                  >
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p
                  className="leading-[1.8]"
                  style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
                >
                  {review.comment}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}